import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, AlertTriangle } from "lucide-react";

const emptyPart = { name: "", sku: "", stock: 0 };

export default function Parts() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPart);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/parts");
    setParts(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyPart);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, sku: p.sku || "", stock: p.stock });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      const payload = {
        ...form,
        stock: parseInt(form.stock) || 0,
      };
      if (editing) {
        await api.put(`/parts/${editing.id}`, payload);
        toast.success("Part updated");
      } else {
        await api.post("/parts", payload);
        toast.success("Part added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to save part");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/parts/${confirmDelete.id}`);
      toast.success("Part deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Failed to delete part");
    }
  };

  return (
    <div>
      <PageHeader
        testid="parts-header"
        title="Parts Inventory"
        subtitle="Track stock levels and low-inventory alerts."
        actions={
          <Button
            data-testid="add-part-btn"
            onClick={openCreate}
            className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl font-medium"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Part
          </Button>
        }
      />

      {loading ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">Loading…</Card>
      ) : parts.length === 0 ? (
        <EmptyState
          testid="parts-empty"
          image="https://images.unsplash.com/photo-1702146715471-ae6b10689969?crop=entropy&cs=srgb&fm=jpg&w=400&q=70"
          title="No parts yet"
          description="Add your commonly used parts to speed up work order creation."
          action={
            <Button
              onClick={openCreate}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="empty-add-part"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Part
            </Button>
          }
        />
      ) : (
        <Card className="rounded-2xl border-warm shadow-card overflow-hidden" data-testid="parts-table">
          <Table>
            <TableHeader className="bg-[#F3F2EE]">
              <TableRow>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Part</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">SKU</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Stock</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {parts.map((p) => {
                const low = p.stock <= 5;
                return (
                  <TableRow key={p.id} data-testid={`part-row-${p.id}`} className="hover:bg-[#FAF9F6]">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#F3F2EE] flex items-center justify-center text-[#3D405B]">
                          <Package className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-[#2B2D42]">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-slate-muted">{p.sku || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${low ? "text-[#C8912A]" : "text-[#2B2D42]"}`}>
                          {p.stock}
                        </span>
                        {low && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#FCF5E8] text-[#8B6914] border border-[#EBD9A8]">
                            <AlertTriangle className="w-3 h-3" /> Low
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-part-${p.id}`}
                          onClick={() => openEdit(p)}
                          className="rounded-lg text-[#3D405B] hover:bg-[#F3F2EE]"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-part-${p.id}`}
                          onClick={() => setConfirmDelete(p)}
                          className="rounded-lg text-[#E07A5F] hover:bg-[#FAE8E4]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="part-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit Part" : "Add Part"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Name *</Label>
              <Input
                data-testid="part-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Brake Pad Set"
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">SKU</Label>
              <Input
                data-testid="part-sku-input"
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                placeholder="BP-1234"
                className="mt-1.5 rounded-lg border-2 border-warm font-mono"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Stock</Label>
              <Input
                data-testid="part-stock-input"
                type="number"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-xl border-2 border-warm"
              data-testid="cancel-part-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="save-part-btn"
            >
              {editing ? "Save Changes" : "Add Part"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete part?</AlertDialogTitle>
            <AlertDialogDescription>
              Historical repair orders will keep the part's name and quantity, but the link to inventory will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-[#E07A5F] hover:bg-[#C86A4F] text-white rounded-xl"
              data-testid="confirm-delete-part"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
